/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { buildApiUrl } from "../config/api.config";
import { useUser } from "./UserContext";
import {
  APPEARANCE_ONBOARDING_VERSION,
  DEFAULT_APPEARANCE,
  normalizeAppearance,
  readLastInterfaceStyle,
  readAppearanceCache,
  writeLastInterfaceStyle,
  writeAppearanceCache,
} from "../features/appearance/appearancePreferences";

const AppearanceContext = createContext(null);

function applyAppearanceToDocument(value, active) {
  const root = document.documentElement;
  const modalScale = ({ 0.9: 0.9, 1: 1, 1.1: 1.15, 1.2: 1.3, 1.3: 1.45 })[value.modernFontScale] || 1;
  root.classList.toggle("modern-interface", active && value.interfaceStyle === "modern");
  root.classList.toggle("classic-interface", active && value.interfaceStyle === "classic");
  if (!active) {
    delete root.dataset.interface;
    root.style.removeProperty("--app-font-scale");
    root.style.removeProperty("--app-modal-font-scale");
    return;
  }
  root.dataset.interface = value.interfaceStyle;
  root.style.setProperty("--app-font-scale", String(value.interfaceStyle === "modern" ? value.modernFontScale : 1));
  root.style.setProperty("--app-modal-font-scale", String(value.interfaceStyle === "modern" ? modalScale : 1));
}

export function AppearanceProvider({ children }) {
  const { isAuthenticated, userId, user } = useUser();
  // Older persisted sessions can contain user.id without the duplicated
  // userId field. Resolve both shapes so database preferences always hydrate.
  const activeUserId = userId || user?.id || null;
  const cached = useMemo(() => readAppearanceCache(activeUserId), [activeUserId]);
  const [preferences, setPreferences] = useState(() => cached || (isAuthenticated
    ? DEFAULT_APPEARANCE
    : { ...DEFAULT_APPEARANCE, interfaceStyle: readLastInterfaceStyle() }));
  const [isHydrating, setIsHydrating] = useState(Boolean(isAuthenticated));
  const [hydratedUserId, setHydratedUserId] = useState(null);
  const [persistenceWarning, setPersistenceWarning] = useState("");
  const [isSwitchingInterface, setIsSwitchingInterface] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);
  const requestIdRef = useRef(0);
  const transitionFallbackTimerRef = useRef(null);
  const transitionCloseTimerRef = useRef(null);
  const transitionStartedAtRef = useRef(0);

  useEffect(() => {
    applyAppearanceToDocument(preferences, isAuthenticated);
    if (isAuthenticated && activeUserId) {
      writeAppearanceCache(activeUserId, preferences);
      writeLastInterfaceStyle(preferences.interfaceStyle);
    }
  }, [activeUserId, isAuthenticated, preferences]);

  useEffect(() => {
    if (!isAuthenticated || !activeUserId) {
      setPreferences({ ...DEFAULT_APPEARANCE, interfaceStyle: readLastInterfaceStyle() });
      setIsHydrating(false);
      setHydratedUserId(null);
      setPersistenceWarning("");
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const cachedValue = readAppearanceCache(activeUserId);
    if (cachedValue) setPreferences(cachedValue);
    setIsHydrating(true);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);
    fetch(buildApiUrl("api/appearance_preferences.php"), {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.success) throw new Error(payload.error || "Appearance preferences could not be loaded.");
        return normalizeAppearance(payload.data);
      })
      .then((next) => {
        if (requestIdRef.current !== requestId) return;
        setPreferences(next);
        writeAppearanceCache(activeUserId, next);
        setPersistenceWarning("");
      })
      .catch((error) => {
        if (error.name === "AbortError" || requestIdRef.current !== requestId) return;
        const safeFallback = cachedValue || DEFAULT_APPEARANCE;
        setPreferences(safeFallback);
        setPersistenceWarning("Using saved appearance on this device. Cloud preference sync is temporarily unavailable.");
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (requestIdRef.current === requestId) {
          setIsHydrating(false);
          setHydratedUserId(activeUserId);
        }
      });

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [activeUserId, isAuthenticated]);

  const savePreferences = useCallback(async (updates) => {
    const previous = preferences;
    const next = normalizeAppearance({ ...previous, ...updates }, previous);
    const interfaceChanged = next.interfaceStyle !== previous.interfaceStyle;
    if (interfaceChanged) {
      window.clearTimeout(transitionFallbackTimerRef.current);
      window.clearTimeout(transitionCloseTimerRef.current);
      transitionStartedAtRef.current = performance.now();
      setSwitchingTo(next.interfaceStyle);
      setIsSwitchingInterface(true);
      // Recovery guard only. The normal close is triggered after the newly
      // selected AppearancePage has resolved its lazy bundle and committed.
      transitionFallbackTimerRef.current = window.setTimeout(() => {
        setIsSwitchingInterface(false);
        setSwitchingTo(null);
      }, 7000);
    }
    setPreferences(next);
    writeAppearanceCache(activeUserId, next);
    writeLastInterfaceStyle(next.interfaceStyle);

    try {
      const response = await fetch(buildApiUrl("api/appearance_preferences.php"), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) throw new Error(payload.error || "Appearance preferences could not be saved.");
      const persisted = normalizeAppearance(payload.data, next);
      setPreferences(persisted);
      writeAppearanceCache(activeUserId, persisted);
      setPersistenceWarning("");
      return { persisted: true, value: persisted };
    } catch (error) {
      // Keep the local choice so a temporary connection issue never traps the user.
      setPersistenceWarning(`${error.message} Your choice is saved on this device and will be retried when you apply it again.`);
      return { persisted: false, value: next, error };
    }
  }, [activeUserId, preferences]);

  const markInterfaceReady = useCallback((readyStyle) => {
    if (!switchingTo || readyStyle !== switchingTo) return;
    window.clearTimeout(transitionFallbackTimerRef.current);
    window.clearTimeout(transitionCloseTimerRef.current);
    const elapsed = performance.now() - transitionStartedAtRef.current;
    const remaining = Math.max(0, 1550 - elapsed);
    transitionCloseTimerRef.current = window.setTimeout(() => {
      setIsSwitchingInterface(false);
      setSwitchingTo(null);
    }, remaining);
  }, [switchingTo]);

  useEffect(() => () => {
    window.clearTimeout(transitionFallbackTimerRef.current);
    window.clearTimeout(transitionCloseTimerRef.current);
  }, []);

  const resetAppearance = useCallback(() => savePreferences({
    ...DEFAULT_APPEARANCE,
    interfaceChoiceCompleted: true,
    modernFontSetupCompleted: true,
    appearanceOnboardingVersion: APPEARANCE_ONBOARDING_VERSION,
  }), [savePreferences]);
  const needsInterfaceChoice = !preferences.interfaceChoiceCompleted
    || preferences.appearanceOnboardingVersion < APPEARANCE_ONBOARDING_VERSION;
  const needsModernFontSetup = preferences.interfaceStyle === "modern" && !preferences.modernFontSetupCompleted;

  const appearanceIsHydrating = isHydrating || Boolean(isAuthenticated && activeUserId && hydratedUserId !== activeUserId);
  const value = useMemo(() => ({
    ...preferences,
    isHydrating: appearanceIsHydrating,
    persistenceWarning,
    needsInterfaceChoice,
    needsModernFontSetup,
    isSwitchingInterface,
    switchingTo,
    markInterfaceReady,
    savePreferences,
    resetAppearance,
  }), [appearanceIsHydrating, isSwitchingInterface, markInterfaceReady, needsInterfaceChoice, needsModernFontSetup, persistenceWarning, preferences, resetAppearance, savePreferences, switchingTo]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance() {
  const context = useContext(AppearanceContext);
  if (!context) throw new Error("useAppearance must be used within an AppearanceProvider");
  return context;
}
